import React, { FC, Fragment, useEffect, useState, useRef } from 'react';
import { CORE_STATUSES, DATE_FORMAT, EVENT_TYPE } from '../../../../constants/constants';
import * as types from '../../../../constants/actionTypes';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import Notification from '../../../Common/Notification';
import { Row, Col } from 'react-bootstrap';
import {
	CardWrapper,
	TaskName,
	CommunityName,
	HorizontalLine,
	SectionTitle,
	SectionSubtitle,
	DateFormat,
	StatusName,
	LeftDot,
	Signal,
	StatusDrop,
	StatusArea,
	DropdownMenu,
	DropdownItem,
} from './styles';
import { actions } from '../../../../actions/locationTaskActions';
import Icon from '../../../Common/Icon';

interface Task {
	isMilestone: boolean;
	name: string;
	communityName: string;
	projectName?: string;
	startDate: string;
	targetDate: string;
	responsibleUserName?: string;
	jobTitleName?: string;
	departmentName: string;
	anticipatedEffort?: string;
	locationTaskId: string;
	statusId: number;
	coreStatusId: number;
	id: number;
}

interface Status {
	coreStatusId: number;
	id: number;
	name: string;
}

interface QuickViewProps {
	task: Task;
	isProjectWidgetActive: boolean;
	onShowMore: () => void;
	onStatusChangedComplete: () => void;
	statuses: Array<Status>;
}

const QuickView: FC<QuickViewProps> = ({ task, isProjectWidgetActive, onShowMore, onStatusChangedComplete, statuses }) => {
	const dispatch = useDispatch();
	const [currentStatus, setCurrentStatus] = useState<Status | null>(null);
	const [isDropdownVisible, setDropdownVisible] = useState(false);

	const dropdownRef = useRef<HTMLDivElement>(null);

	const isPastDue = moment().isAfter(moment.utc(task.targetDate));

	const toggleDropdown = () => {
		setDropdownVisible(!isDropdownVisible);
	};

	const changeStatus = async (ids: Array<string>, status: Status) => {
		try {
			const result = await dispatch(actions.updateStatus(ids, status.id));

			if (result.type === types.UPDATE_LOCATION_TASK_LIST_STATUS_SUCCESS) {
				Notification('success', '', 'The Status was updated.');
				setCurrentStatus(status);
				onStatusChangedComplete();
				setDropdownVisible(false);
				if (status.coreStatusId === CORE_STATUSES.Complete) {
					const event = new Event(EVENT_TYPE.ConfettiCannon);
					document.dispatchEvent(event);
				}
			} else if (result.type === types.UPDATE_LOCATION_TASK_LIST_STATUS_FAILURE) {
				Notification('error', 'An Error Occurred', result.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setDropdownVisible(false);
			}
		};

		// Add event listener for clicks
		document.addEventListener('mousedown', handleClickOutside);

		// Clean up the event listener when the component unmounts
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [dropdownRef]);

	useEffect(() => {
		if (statuses.length > 0 && task.statusId) {
			const status = statuses.find((status) => status.id === task.statusId);
			if (status) {
				setCurrentStatus(status);
			}
		}
	}, [statuses, task.statusId]);

	return (
		<CardWrapper>
			<TaskName onClick={onShowMore}>
				<span className='name'>{task.name}</span>
				{task.isMilestone && <Icon name='bookmark' stylePrefix='fa-solid' />}
			</TaskName>
			<CommunityName>{task.communityName}</CommunityName>

			<HorizontalLine />

			{currentStatus && (
				<>
					<StatusArea>
						<Signal>
							{currentStatus.coreStatusId == 2 ? (
								<Icon name='ban' stylePrefix='fa-regular fa-xs'></Icon>
							) : (
								<LeftDot coreStatusId={currentStatus.coreStatusId}></LeftDot>
							)}
						</Signal>

						<StatusName>{currentStatus.name}</StatusName>
						<StatusDrop onClick={toggleDropdown}>
							<Icon name='chevron-down' stylePrefix='fa-regular fa-xl'></Icon>
						</StatusDrop>

						{/* Dynamic Dropdown Menu */}
						{isDropdownVisible && (
							<DropdownMenu ref={dropdownRef}>
								{statuses.map((status) => (
									<DropdownItem key={status.id} onClick={() => changeStatus([task.id], status)}>
										<Signal>
											{status.coreStatusId == 2 ? (
												<Icon name='ban' stylePrefix='fa-regular fa-xs'></Icon>
											) : (
												<LeftDot coreStatusId={status.coreStatusId}></LeftDot>
											)}
										</Signal>
										<StatusName>{status.name}</StatusName>
									</DropdownItem>
								))}
							</DropdownMenu>
						)}
					</StatusArea>

					<HorizontalLine />
				</>
			)}

			{isProjectWidgetActive && task.projectName && (
				<>
					<SectionTitle>{task.projectName}</SectionTitle>
					<SectionSubtitle>Project Name</SectionSubtitle>
					<HorizontalLine />
				</>
			)}
			<Row>
				<Col>
					<DateFormat isPastDue={false}>
						<span>{moment.utc(task.startDate).format(DATE_FORMAT)}</span>
						<span>Start Date</span>
					</DateFormat>
				</Col>
				<Col>
					<DateFormat isPastDue={isPastDue}>
						<span>{moment.utc(task.targetDate).format(DATE_FORMAT)}</span>
						<span>Target Date</span>
					</DateFormat>
				</Col>
			</Row>

			<HorizontalLine />

			<>
				<SectionTitle>{task.responsibleUserName ? task.responsibleUserName : 'Unassigned'}</SectionTitle>
				<SectionSubtitle>{task.jobTitleName ? task.jobTitleName : 'Responsible User'}</SectionSubtitle>
				<HorizontalLine />
			</>

			<SectionTitle>{task.departmentName}</SectionTitle>
			<SectionSubtitle>Department</SectionSubtitle>

			{task.anticipatedEffort && (
				<>
					<HorizontalLine />
					<SectionTitle>{task.anticipatedEffort}</SectionTitle>
					<SectionSubtitle>Anticipated Effort</SectionSubtitle>
				</>
			)}
		</CardWrapper>
	);
};

export default QuickView;
